import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";

const BINANCE_REFERRAL = "https://www.binance.com/register?ref=KXMN4JF5";

const COIN_MAP = {
  bitcoin:   { symbol: "BTCUSDT",    ticker: "BTC",    name: "Bitcoin",       coingecko: "bitcoin" },
  ethereum:  { symbol: "ETHUSDT",    ticker: "ETH",    name: "Ethereum",      coingecko: "ethereum" },
  bnb:       { symbol: "BNBUSDT",    ticker: "BNB",    name: "BNB",           coingecko: "binancecoin" },
  xrp:       { symbol: "XRPUSDT",    ticker: "XRP",    name: "XRP",           coingecko: "ripple" },
  solana:    { symbol: "SOLUSDT",    ticker: "SOL",    name: "Solana",        coingecko: "solana" },
  cardano:   { symbol: "ADAUSDT",    ticker: "ADA",    name: "Cardano",       coingecko: "cardano" },
  dogecoin:  { symbol: "DOGEUSDT",   ticker: "DOGE",   name: "Dogecoin",      coingecko: "dogecoin" },
  tron:      { symbol: "TRXUSDT",    ticker: "TRX",    name: "TRON",          coingecko: "tron" },
  avalanche: { symbol: "AVAXUSDT",   ticker: "AVAX",   name: "Avalanche",     coingecko: "avalanche-2" },
  polkadot:  { symbol: "DOTUSDT",    ticker: "DOT",    name: "Polkadot",      coingecko: "polkadot" },
  chainlink: { symbol: "LINKUSDT",   ticker: "LINK",   name: "Chainlink",     coingecko: "chainlink" },
  litecoin:  { symbol: "LTCUSDT",    ticker: "LTC",    name: "Litecoin",      coingecko: "litecoin" },
  polygon:   { symbol: "POLUSDT",    ticker: "POL",    name: "Polygon",       coingecko: "matic-network" },
  uniswap:   { symbol: "UNIUSDT",    ticker: "UNI",    name: "Uniswap",       coingecko: "uniswap" },
  near:      { symbol: "NEARUSDT",   ticker: "NEAR",   name: "NEAR Protocol", coingecko: "near" },
  atom:      { symbol: "ATOMUSDT",   ticker: "ATOM",   name: "Cosmos",        coingecko: "cosmos" },
  arbitrum:  { symbol: "ARBUSDT",    ticker: "ARB",    name: "Arbitrum",      coingecko: "arbitrum" },
  optimism:  { symbol: "OPUSDT",     ticker: "OP",     name: "Optimism",      coingecko: "optimism" },
  injective: { symbol: "INJUSDT",    ticker: "INJ",    name: "Injective",     coingecko: "injective-protocol" },
  sui:       { symbol: "SUIUSDT",    ticker: "SUI",    name: "Sui",           coingecko: "sui" },
  aptos:     { symbol: "APTUSDT",    ticker: "APT",    name: "Aptos",         coingecko: "aptos" },
  filecoin:  { symbol: "FILUSDT",    ticker: "FIL",    name: "Filecoin",      coingecko: "filecoin" },
  stellar:   { symbol: "XLMUSDT",    ticker: "XLM",    name: "Stellar",       coingecko: "stellar" },
  vechain:   { symbol: "VETUSDT",    ticker: "VET",    name: "VeChain",       coingecko: "vechain" },
  algorand:  { symbol: "ALGOUSDT",   ticker: "ALGO",   name: "Algorand",      coingecko: "algorand" },
  sandbox:   { symbol: "SANDUSDT",   ticker: "SAND",   name: "The Sandbox",   coingecko: "the-sandbox" },
  mana:      { symbol: "MANAUSDT",   ticker: "MANA",   name: "Decentraland",  coingecko: "decentraland" },
  aave:      { symbol: "AAVEUSDT",   ticker: "AAVE",   name: "Aave",          coingecko: "aave" },
  render:    { symbol: "RENDERUSDT", ticker: "RENDER", name: "Render",        coingecko: "render-token" },
  pepe:      { symbol: "PEPEUSDT",   ticker: "PEPE",   name: "Pepe",          coingecko: "pepe" },
};


const INTERVALS = [
  { label: "1H", value: "1h", limit: 168 },
  { label: "4H", value: "4h", limit: 180 },
  { label: "1D", value: "1d", limit: 365 },
  { label: "1W", value: "1w", limit: 52 },
];

function formatNumber(n) {
  if (!n) return "N/A";
  if (n >= 1e12) return "$" + (n / 1e12).toFixed(2) + "T";
  if (n >= 1e9) return "$" + (n / 1e9).toFixed(2) + "B";
  if (n >= 1e6) return "$" + (n / 1e6).toFixed(2) + "M";
  return "$" + n.toLocaleString();
}

function formatPrice(price) {
  if (!price) return "N/A";
  if (price < 1) return "$" + price.toFixed(6);
  return "$" + price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function BinanceButton({ ticker, className }) {
  const handleClick = () => {
    window.open(BINANCE_REFERRAL, "_blank", "noopener,noreferrer");
  };
  return (
    <button onClick={handleClick} className={className}>
      {"Trade " + ticker + " on Binance"}
    </button>
  );
}

function BinanceCTA({ name }) {
  const handleClick = () => {
    window.open(BINANCE_REFERRAL, "_blank", "noopener,noreferrer");
  };
  return (
    <div className="mt-10 bg-[#0a0a0b] border border-yellow-400/20 p-6 flex flex-col md:flex-row items-center justify-between gap-4">
      <div>
        <p className="font-display font-bold text-white text-lg">{"Ready to trade " + name + "?"}</p>
        <p className="text-zinc-400 text-sm mt-1">Get started on Binance — the world's largest crypto exchange.</p>
      </div>
      <button
        onClick={handleClick}
        className="bg-yellow-400 text-black font-bold uppercase tracking-wide px-6 py-3 hover:bg-yellow-500 transition whitespace-nowrap"
      >
        Trade on Binance
      </button>
    </div>
  );
}

export default function CoinDetail() {
  const { slug } = useParams();
  const coin = COIN_MAP[slug];
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const candleSeriesRef = useRef(null);
  const wsRef = useRef(null);
  const [activeInterval, setActiveInterval] = useState("1d");
  const [livePrice, setLivePrice] = useState(null);
  const [change24h, setChange24h] = useState(null);
  const [marketData, setMarketData] = useState(null);
  const [news, setNews] = useState([]);
  const [chartReady, setChartReady] = useState(false);

  useEffect(() => {
    if (!coin) return;
    const url = "https://api.coingecko.com/api/v3/coins/" + coin.coingecko + "?localization=false&tickers=false&community_data=false&developer_data=false";
    fetch(url)
      .then((r) => r.json())
      .then((data) => {
        setMarketData(data);
        setLivePrice(data?.market_data?.current_price?.usd);
        setChange24h(data?.market_data?.price_change_percentage_24h);
      })
      .catch(() => {});
  }, [coin]);

  useEffect(() => {
    if (!coin) return;
    const catMap = {
      bitcoin: "bitcoin", ethereum: "ethereum", bnb: "altcoins",
      xrp: "altcoins", solana: "altcoins", cardano: "altcoins",
      dogecoin: "altcoins", polygon: "altcoins", litecoin: "altcoins", chainlink: "altcoins",
    };
    api.get("/news?category=" + catMap[slug] + "&limit=4")
      .then((r) => setNews(r.data.slice(0, 3)))
      .catch(() => {});
  }, [slug, coin]);

  useEffect(() => {
    if (!chartRef.current || !coin) return;
    let chart;
    const timer = setTimeout(() => {
      import("lightweight-charts").then(({ createChart }) => {
        if (!chartRef.current) return;
        chart = createChart(chartRef.current, {
          width: chartRef.current.clientWidth,
          height: 320,
          layout: { background: { color: "#0a0a0b" }, textColor: "#a1a1aa" },
          grid: { vertLines: { color: "#ffffff10" }, horzLines: { color: "#ffffff10" } },
          crosshair: { mode: 1 },
          rightPriceScale: { borderColor: "#ffffff20" },
          timeScale: { borderColor: "#ffffff20", timeVisible: true },
        });
        const series = chart.addCandlestickSeries({
          upColor: "#22c55e", downColor: "#ef4444",
          borderUpColor: "#22c55e", borderDownColor: "#ef4444",
          wickUpColor: "#22c55e", wickDownColor: "#ef4444",
        });
        chartInstance.current = chart;
        candleSeriesRef.current = series;
        setChartReady(true);
        const handleResize = () => {
          if (chartRef.current) chart.applyOptions({ width: chartRef.current.clientWidth });
        };
        window.addEventListener("resize", handleResize);
      });
    }, 100);
    return () => {
      clearTimeout(timer);
      if (chart) chart.remove();
    };
  }, [coin]);

  useEffect(() => {
    if (!coin || !chartReady || !candleSeriesRef.current) return;
    const int = INTERVALS.find((i) => i.value === activeInterval);
    const url = "https://api.binance.com/api/v3/klines?symbol=" + coin.symbol + "&interval=" + activeInterval + "&limit=" + int.limit;
    fetch(url)
      .then((r) => r.json())
      .then((data) => {
        if (!Array.isArray(data)) return;
        const candles = data.map((d) => ({
          time: Math.floor(d[0] / 1000),
          open: parseFloat(d[1]),
          high: parseFloat(d[2]),
          low: parseFloat(d[3]),
          close: parseFloat(d[4]),
        }));
        candleSeriesRef.current.setData(candles);
        chartInstance.current.timeScale().fitContent();
      })
      .catch(() => {});
  }, [coin, activeInterval, chartReady]);

  useEffect(() => {
    if (!coin) return;
    if (wsRef.current) wsRef.current.close();
    const ws = new WebSocket("wss://stream.binance.com:9443/ws/" + coin.symbol.toLowerCase() + "@ticker");
    ws.onmessage = (e) => {
      const d = JSON.parse(e.data);
      setLivePrice(parseFloat(d.c));
      setChange24h(parseFloat(d.P));
    };
    wsRef.current = ws;
    return () => ws.close();
  }, [coin]);

  if (!coin) {
    return (
      <div className="pt-32 text-center text-zinc-500 font-mono">
        Coin not found.
      </div>
    );
  }

  const isPositive = (change24h || 0) >= 0;
  const md = marketData?.market_data;

  const stats = [
    { label: "Market Cap",         value: formatNumber(md?.market_cap?.usd) },
    { label: "24h Volume",         value: formatNumber(md?.total_volume?.usd) },
    { label: "24h High",           value: formatPrice(md?.high_24h?.usd) },
    { label: "24h Low",            value: formatPrice(md?.low_24h?.usd) },
    { label: "Circulating Supply", value: md?.circulating_supply ? md.circulating_supply.toLocaleString() + " " + coin.ticker : "N/A" },
    { label: "Total Supply",       value: md?.total_supply ? md.total_supply.toLocaleString() + " " + coin.ticker : "N/A" },
    { label: "All Time High",      value: formatPrice(md?.ath?.usd) },
    { label: "ATH Change",         value: md?.ath_change_percentage?.usd ? md.ath_change_percentage.usd.toFixed(1) + "%" : "N/A" },
  ];

  return (
    <div className="pt-10 pb-20">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            {marketData?.image?.large && (
              <img src={marketData.image.large} alt={coin.name} className="w-10 h-10 md:w-12 md:h-12" />
            )}
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display font-bold text-2xl md:text-4xl text-white">{coin.name}</h1>
                <span className="font-mono text-xs text-zinc-500 bg-zinc-900 px-2 py-1">{coin.ticker}</span>
                {md?.market_cap_rank && (
                  <span className="font-mono text-xs text-yellow-400 border border-yellow-400/30 px-2 py-1">
                    {"#" + md.market_cap_rank}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1">
                <span className="font-display font-bold text-2xl md:text-3xl text-white">
                  {formatPrice(livePrice)}
                </span>
                {change24h !== null && (
                  <span className={"flex items-center gap-1 font-mono text-sm font-bold " + (isPositive ? "text-green-400" : "text-red-400")}>
                    {isPositive ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                    {Math.abs(change24h).toFixed(2) + "%"}
                  </span>
                )}
              </div>
            </div>
          </div>
          <BinanceButton
  ticker={coin.ticker}
  className="hidden md:block bg-yellow-400 text-black font-bold uppercase tracking-wide px-5 py-2 hover:bg-yellow-500 transition text-sm w-fit"
/>
        </div>

        {/* Chart + Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
          <div className="lg:col-span-8 bg-[#0a0a0b] border border-white/10 p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-lg text-white">{coin.name + " Price Chart"}</h2>
              <div className="flex gap-1">
                {INTERVALS.map((i) => (
                  <button
                    key={i.value}
                    onClick={() => setActiveInterval(i.value)}
                    className={"px-2 py-1 text-xs font-mono uppercase tracking-wide transition " + (activeInterval === i.value ? "bg-yellow-400 text-black" : "border border-white/20 text-zinc-400 hover:border-yellow-400 hover:text-yellow-400")}
                  >
                    {i.label}
                  </button>
                ))}
              </div>
            </div>
            <div ref={chartRef} style={{ width: "100%", height: "320px" }} />
          </div>

          <div className="lg:col-span-4 space-y-2">
            {stats.map((s) => (
              <div key={s.label} className="bg-[#0a0a0b] border border-white/10 px-4 py-3 flex items-center justify-between">
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">{s.label}</div>
                <div className="font-display font-bold text-white text-sm text-right">{s.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* About */}
        {marketData?.description?.en && (
          <div className="bg-[#0a0a0b] border border-white/10 p-6 mb-8">
            <h2 className="font-display font-bold text-xl text-white mb-4">{"About " + coin.name}</h2>
            <p className="text-zinc-300 leading-relaxed text-sm line-clamp-6">
              {marketData.description.en.replace(/<[^>]+>/g, "")}
            </p>
          </div>
        )}

        {/* Related News */}
        {news.length > 0 && (
          <div className="mb-8">
            <h2 className="font-display font-bold text-2xl text-white mb-6">
              <span className="text-yellow-400">/</span>
              {" Latest " + coin.name + " News"}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {news.map((n) => (
                <Link
                  key={n.id}
                  to={"/news/" + n.id}
                  className="block bg-[#0a0a0b] border border-white/10 hover:border-yellow-400/40 transition p-5 group"
                >
                  {n.image_url && (
                    <img src={n.image_url} alt={n.title} className="w-full h-32 object-cover mb-4" />
                  )}
                  <h3 className="font-display font-bold text-white group-hover:text-yellow-400 transition">{n.title}</h3>
                  <p className="mt-2 text-sm text-zinc-400 line-clamp-2">{n.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        <BinanceCTA name={coin.name} />

      </div>
    </div>
  );
}